"use client";

import Image from "next/image";
import Link from "next/link";
import { createPortal } from "react-dom";
import { type ReactNode, useEffect, useId, useState } from "react";

import { NavMenu } from "@/components/ownly/NavMenu";
import { BOOKING_CTA, OWNLY_BOOKING_HREF } from "@/lib/booking";
import { BUILT_GROUPS } from "@/lib/built-catalogue";
import { CASE_STUDIES } from "@/lib/case-studies";
import { INDUSTRIES } from "@/lib/industries";
import { SERVICE_CATEGORIES, servicesInCategory } from "@/lib/services";
import { OWNLY_OFFER, OWNLY_PROJECT_HREF } from "@/lib/site";

const navLinkClass =
  "rounded-full px-2.5 py-1.5 text-sm font-semibold tracking-tight text-white/78 transition-[color,background-color] duration-300 hover:bg-white/[0.09] hover:text-white sm:px-3 sm:text-sm md:px-4 md:py-2 md:text-sm";

const navLinkCtaClass =
  "rounded-full px-2.5 py-1.5 text-sm font-semibold tracking-tight text-ownly-accent-text transition-[color,background-color] duration-300 hover:bg-ownly-accent/15 hover:text-[#5ca3ff] sm:px-3 sm:text-sm md:px-4 md:py-2 md:text-sm";

/*
 * WHAT SITS UNDER EACH TOP-LEVEL ITEM.
 *
 * Built from the same registries the pages are, so a service or an industry cannot be added
 * and left out of the navigation, which is how a menu ends up describing the site as it was
 * eight months ago. Work is the exception and is hand-picked: it lists the studies worth
 * opening first rather than all nine in the order they happen to be declared.
 *
 * Process keeps no panel. It has no children, and a dropdown holding one link is a dropdown
 * that teaches people the menus are not worth opening.
 */
const workSections = [
  {
    heading: "Recent work",
    links: CASE_STUDIES.slice(0, 5).map((study) => ({
      label: study.name,
      href: `/work/${study.slug}`,
      note: study.businessType,
    })),
  },
  {
    heading: "Proof",
    links: [
      { label: "All case studies", href: "/work", note: "Every project, with the brief and what changed" },
      { label: "What we have built", href: "/built", note: "The delivered feature library" },
      ...BUILT_GROUPS.slice(0, 3).map((group) => ({
        label: group.name,
        href: `/built/${group.slug}`,
      })),
    ],
  },
];

/*
 * The Operations Day (/ai-operations-consulting) is deliberately NOT in any menu. Tom,
 * 2026-10-02: "We're mostly just pitching software. The consultancy thing's more for me to
 * deal with." The page still works at its URL (noindex) so Tom can send it to people himself.
 * Its slot in the first group went to How a project goes, so the group is not one lonely link.
 */
const serviceSections = [
  {
    heading: "Ways to work with Ownly",
    links: [
      {
        label: "Custom software builds",
        href: "/custom-software-for-businesses",
        note: "Complete apps, platforms and business systems",
      },
      {
        label: "How a project goes",
        href: "/process",
        note: "From the first call to the system you run on",
      },
    ],
  },
  ...SERVICE_CATEGORIES.map((category) => ({
    heading: category.name,
    links: servicesInCategory(category.slug).map((service) => ({
      label: service.name,
      href: service.href,
    })),
  })),
];

/* Two columns since 2026-09-19. At eight industries a single column ran past the bottom of a
   720px laptop screen and cut off "All industries", so the list is split in half rather than
   made to scroll inside a hover menu. */
const industryLinks = INDUSTRIES.map((industry) => ({
  label: industry.name,
  href: industry.href,
  note: industry.hubBlurb,
}));
const industryHalf = Math.ceil(industryLinks.length / 2);
const industrySections = [
  { links: industryLinks.slice(0, industryHalf) },
  { links: industryLinks.slice(industryHalf) },
];

const mobileNavLinkClass =
  "block w-full rounded-xl px-4 py-3.5 text-left text-body font-semibold tracking-tight text-white/90 transition-colors hover:bg-white/[0.06] active:bg-white/[0.08]";

const mobileNavCtaClass =
  "block w-full rounded-xl px-4 py-3.5 text-left text-body font-semibold tracking-tight text-ownly-accent-text transition-colors hover:bg-ownly-accent/12 active:bg-ownly-accent/18";

const mobileSubLinkClass =
  "block w-full rounded-lg px-4 py-2.5 text-left text-body font-medium tracking-tight text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white active:bg-white/[0.08]";

/**
 * One collapsible group in the mobile menu.
 *
 * A native `<details>`, which is the whole reason it is safe: it opens with no JavaScript, it
 * is already a disclosure to a screen reader, and there is no state of ours that can wedge it
 * open or shut. The chevron is CSS off the open attribute rather than a second source of truth.
 */
function MobileNavGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <details className="group/nav border-b border-white/[0.07] last:border-b-0">
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-4 py-3.5 text-body font-semibold tracking-tight text-white/90 transition-colors marker:content-none hover:bg-white/[0.06] [&::-webkit-details-marker]:hidden">
        {label}
        <svg
          viewBox="0 0 24 24"
          className="size-4 shrink-0 text-white/50 transition-transform duration-200 group-open/nav:rotate-180"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </summary>
      <div className="pb-2">{children}</div>
    </details>
  );
}

type MobileNavOverlayProps = {
  menuId: string;
  onClose: () => void;
  hideProjectCta?: boolean;
};

/**
 * Full-screen mobile nav: opaque layer above page chrome, own header row.
 * Do not use `md:hidden` here — at ~768px CSS px (zoom / tablet / devtools) the panel can
 * stay open while that utility hides the overlay entirely (hero + X, no links).
 */
function MobileNavOverlay({ menuId, onClose, hideProjectCta = false }: MobileNavOverlayProps) {
  return (
    <div
      id={menuId}
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      className="fixed inset-0 z-[10000] flex max-h-[100dvh] min-h-0 w-full min-w-0 flex-col bg-ownly-bg"
    >
      <div className="flex h-[5.25rem] shrink-0 items-center justify-between gap-3 border-b border-white/[0.08] px-4 sm:px-6">
        <Link
          href="/"
          onClick={onClose}
          className="flex min-w-0 shrink-0 items-center rounded-full border border-white/[0.12] bg-white/[0.035] px-2.5 py-2 shadow-[0_8px_28px_-16px_rgba(0,0,0,0.65),inset_0_1px_0_0_rgba(255,255,255,0.06)]"
        >
          <Image
            src="/brand/ownly-wordmark.png"
            alt="Ownly"
            width={1024}
            height={307}
            sizes="(max-width:767px) 180px, 220px"
            className="!h-9 !max-w-none w-auto object-contain drop-shadow-[0_0_14px_rgba(43,127,255,0.12)] sm:!h-10"
          />
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.035] text-white/85 shadow-[0_8px_28px_-16px_rgba(0,0,0,0.65)] transition-colors hover:bg-white/[0.08]"
          aria-label="Close menu"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <nav
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6 sm:px-6"
        aria-label="Primary mobile"
      >
        <div className="mx-auto flex w-full max-w-md flex-col gap-1">
          {/* THE SAME SUB-NAVIGATION THE DESKTOP PANELS HAVE, which is the point of the menu
              button existing at all: on a phone this is the whole navigation, and sending
              somebody to a hub page to find the thing they already knew the name of is the
              long way round. Native <details>, so it opens without JavaScript, announces
              itself to a screen reader, and cannot get stuck open behind a state bug.

              Each group's own hub is the first link inside it rather than the summary line,
              because a summary that both expands and navigates does neither predictably. */}
          <MobileNavGroup label="Work">
            <Link href="/work" className={mobileSubLinkClass} onClick={onClose}>
              All case studies
            </Link>
            <Link href="/built" className={mobileSubLinkClass} onClick={onClose}>
              What we have built
            </Link>
            {CASE_STUDIES.slice(0, 5).map((study) => (
              <Link
                key={study.slug}
                href={`/work/${study.slug}`}
                className={mobileSubLinkClass}
                onClick={onClose}
              >
                {study.name}
              </Link>
            ))}
          </MobileNavGroup>
          <MobileNavGroup label="Services">
            <Link href="/services" className={mobileSubLinkClass} onClick={onClose}>
              All services
            </Link>
            <p className="px-4 pb-1 pt-3 text-copy font-semibold text-ownly-accent-text">
              Ways to work with Ownly
            </p>
            <Link href="/custom-software-for-businesses" className={mobileSubLinkClass} onClick={onClose}>
              Custom software builds
            </Link>
            <Link href="/process" className={mobileSubLinkClass} onClick={onClose}>
              How a project goes
            </Link>
            {SERVICE_CATEGORIES.map((category) => (
              <div key={category.slug}>
                <p className="px-4 pb-1 pt-3 text-copy font-semibold text-ownly-accent-text">
                  {category.name}
                </p>
                {servicesInCategory(category.slug).map((service) => (
                  <Link
                    key={service.slug}
                    href={service.href}
                    className={mobileSubLinkClass}
                    onClick={onClose}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            ))}
          </MobileNavGroup>
          <MobileNavGroup label="Industries">
            <Link href="/industries" className={mobileSubLinkClass} onClick={onClose}>
              All industries
            </Link>
            {INDUSTRIES.map((industry) => (
              <Link
                key={industry.slug}
                href={industry.href}
                className={mobileSubLinkClass}
                onClick={onClose}
              >
                {industry.name}
              </Link>
            ))}
          </MobileNavGroup>
          <Link href="/people" className={mobileNavLinkClass} onClick={onClose}>
            People
          </Link>
          <Link href="/process" className={mobileNavLinkClass} onClick={onClose}>
            Process
          </Link>
          <Link href="/guides" className={mobileNavLinkClass} onClick={onClose}>
            Guides
          </Link>
          {/* The guide, which was reachable from the nav on no device at all and is the one
              live lead magnet. Deliberately NOT the site check beside it: that offer was
              retired on 2026-08-26 and putting it back in a menu is how a retired thing
              quietly becomes live again. See the note on the desktop bar.

              CALLED "DOWNLOADS", THE SAME AS IN THE FOOTER. It said "Free guides" here and
              "Downloads" there, for the same page, sitting directly beneath "Guides" in this
              list. Tom, on a phone: "there's loads of tabs on the dropdown on mobile". Two
              adjacent items that sound like the same thing are worse than one extra item. */}
          <Link href="/free" className={mobileNavLinkClass} onClick={onClose}>
            Downloads
          </Link>
          <Link href="/faq" className={mobileNavLinkClass} onClick={onClose}>
            FAQ
          </Link>
          <Link href="/careers" className={mobileNavLinkClass} onClick={onClose}>
            Careers
          </Link>
          {/* The mobile menu deliberately keeps every link the desktop bar drops. A
              full-screen list has the room; a pill row next to the logo does not. */}
          <Link href={OWNLY_BOOKING_HREF} className={mobileNavLinkClass} onClick={onClose}>
            {BOOKING_CTA}
          </Link>
          {hideProjectCta ? null : (
            <>
              <Link href={OWNLY_PROJECT_HREF} className={mobileNavCtaClass} onClick={onClose}>
                {OWNLY_OFFER.primaryCta}
              </Link>
              {/* Say what happens next, so this does not read like a commitment. */}
              <p className="mt-2 px-4 text-center text-sm leading-[22px] text-white/55">
                A practical video, real work and straight answers before you book.
              </p>
            </>
          )}
        </div>
      </nav>
    </div>
  );
}

export function SiteHeader({ hideProjectCta = false }: { hideProjectCta?: boolean } = {}) {
  const menuId = useId();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  /* Close the menu once the width crosses into the desktop bar, which is where the button
     that opened it disappears. The breakpoint is lg rather than md because the bar itself
     moved: seven items and two calls to action wrapped at 820px. Leaving it open past the
     swap would strand somebody in a full-screen overlay whose only close button is the one
     they can no longer see behind it. */
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* SOLID once scrolled, not 95%. At 95% the big white display headings ghosted through the
          bar in Safari as the page slid under it (Tom, 2026-09-23, on /book). No backdrop blur
          either: blur over moving content is the Safari shake the design rules forbid. */}
      <header
        className={`fixed top-0 right-0 left-0 z-50 w-full transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-b border-white/[0.12] bg-ownly-bg shadow-[0_22px_56px_-28px_rgba(0,0,0,0.62),0_0_80px_-40px_rgba(43,127,255,0.1)]"
            : "border-transparent bg-transparent shadow-none"
        }`}
      >
        {scrolled ? (
          <div
            className="pointer-events-none absolute inset-0 z-0 opacity-[0.09] ownly-section-grid ownly-section-grid-drift"
            aria-hidden
          />
        ) : null}
        {scrolled ? (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-px bg-gradient-to-r from-transparent via-ownly-accent/35 to-transparent"
            aria-hidden
          />
        ) : null}
        {/* `ownly-page-grid`, not a hand-rolled max-width: the logo's left edge IS the site's
            content edge, so the header has to be measured by the same class every page uses. */}
        <div className="ownly-page-grid relative z-10 flex h-[5.25rem] min-w-0 items-center justify-between gap-3 overflow-x-clip transition-[height] duration-300 sm:gap-4 md:h-[5.75rem] md:gap-6 lg:h-24">
          <Link
            href="/"
            className="flex min-w-0 shrink-0 items-center rounded-full border border-white/[0.12] bg-[#0f1829]/90 px-2.5 py-2 shadow-[0_8px_28px_-16px_rgba(0,0,0,0.65),inset_0_1px_0_0_rgba(255,255,255,0.06)] transition-[border-color,background-color,box-shadow] duration-300 hover:border-white/[0.16] hover:bg-white/[0.05] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ownly-accent sm:px-3 md:py-2.5"
          >
            <Image
              src="/brand/ownly-wordmark.png"
              alt="Ownly"
              width={1024}
              height={307}
              priority
              sizes="(max-width:767px) 180px, 220px"
              className="!h-9 !max-w-none w-auto object-contain drop-shadow-[0_0_14px_rgba(43,127,255,0.12)] md:!h-10 lg:!h-11"
            />
          </Link>

          <nav
            className="ml-1 hidden items-center gap-0.5 rounded-full border border-white/[0.12] bg-[#0f1829]/90 p-1 shadow-[0_8px_28px_-16px_rgba(0,0,0,0.65),inset_0_1px_0_0_rgba(255,255,255,0.06)] sm:ml-2 sm:gap-1 sm:p-1.5 lg:ml-0 lg:flex lg:gap-1 lg:p-1"
            aria-label="Primary"
          >
            {/* FIVE items, and it stays five. This bar carried nine plus the CTA, which is
                more than anyone reads and pushed the row into the logo on small laptops.
                The five that survive are the ones a buyer actually moves through: what we
                have made, what we sell, what it costs, how it runs, and something to take
                away if they are not ready yet.

                Ideas (/built), People, FAQ and Careers moved to the footer. All four are
                still in the full-screen mobile menu, which has the room and where a phone
                user genuinely does browse. Adding a sixth here means removing one.

                THE FREE SITE CHECK DOES NOT GO IN HERE, and this is the third time that has
                had to be settled. It was in the nav for about an hour in August before Tom
                removed it, it was put back on 2026-09-06 in place of Process, and it came
                straight back out the same day when he remembered why: the audit was RETIRED
                on 2026-08-26 (commit 4467b7a). Its own retirement note is the argument, and
                it is a good one: six of its checks turned out to be measuring the wrong
                thing, and the offer is wrong even now they are fixed, because its value
                depends on the answer being bad and twenty of twenty four passing tells a
                prospect they are fine. Promoting a retired offer is worse than hiding a live
                one. The live magnet is the guide, and that is what /free is for. */}
            <NavMenu
              label="Work"
              href="/work"
              sections={workSections}
              columns={2}
              linkClass={navLinkClass}
            />
            <NavMenu
              label="Services"
              href="/services"
              sections={serviceSections}
              columns={2}
              linkClass={navLinkClass}
            />
            {/* Added 2026-09-11 with the industries architecture. It sits next to Services
                because the two answer the same question from opposite ends: what we build,
                and who we build it for. Built stays out of this row and keeps its place in
                the mobile menu and the footer: eight items across a 768px header is a worse
                outcome for the proof library than the route it already has. */}
            <NavMenu
              label="Industries"
              href="/industries"
              sections={industrySections}
              columns={2}
              linkClass={navLinkClass}
            />
            <Link href="/process" className={navLinkClass}>
              Process
            </Link>
            {/* Guides is back in this row (2026-10-02). Operations Day took its place on
                2026-09-28 and came out of every menu when Tom decided the site pitches custom
                software only ("We're mostly just pitching software. The consultancy thing's
                more for me to deal with."). Process is shown at every desktop width again,
                as it was before that change. */}
            <Link href="/guides" className={navLinkClass}>
              Guides
            </Link>
            {/* Two ways in, deliberately ranked. The form comes first and keeps the accent
                colour; the call is the lighter option for someone not ready to type. */}
            <Link href={OWNLY_BOOKING_HREF} className={navLinkClass}>
              {BOOKING_CTA}
            </Link>
            {hideProjectCta ? null : (
              <Link href={OWNLY_PROJECT_HREF} className={navLinkCtaClass}>
                {OWNLY_OFFER.primaryCta}
              </Link>
            )}
          </nav>

          {/* The menu button, and the row above it, now swap at lg rather than md. Adding
              Industries made seven items plus two calls to action, and at 820px that wrapped
              both CTAs onto two lines each. The tablet range gets the full-screen menu
              instead, which was already the better experience at that width. */}
          <div className="relative shrink-0 lg:hidden">
            <button
              type="button"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-[#0f1829]/90 px-3 shadow-[0_8px_28px_-16px_rgba(0,0,0,0.65),inset_0_1px_0_0_rgba(255,255,255,0.06)] transition-[border-color,background-color] duration-300 hover:border-white/[0.16] hover:bg-white/[0.05] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ownly-accent"
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="sr-only">{menuOpen ? "Close" : "Menu"}</span>
              {menuOpen ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white/85"
                  aria-hidden
                >
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white/85"
                  aria-hidden
                >
                  <path
                    d="M5 7H19M5 12H19M5 17H19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
        {/* Reading progress. Driven by the document's own scroll timeline, so it needs no
            measurement and cannot drift out of step with the page. */}
        <div
          className="ownly-progress-bar absolute inset-x-0 bottom-0 h-px origin-left bg-gradient-to-r from-ownly-accent via-ownly-violet to-transparent"
          aria-hidden
        />
      </header>

      {typeof document !== "undefined" && menuOpen
        ? createPortal(
            <MobileNavOverlay
              menuId={menuId}
              onClose={closeMenu}
              hideProjectCta={hideProjectCta}
            />,
            document.body,
          )
        : null}
    </>
  );
}
